import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, MessageSquare, PlayCircle } from 'lucide-react';
import AnimatedCounter from '../components/AnimatedCounter';
import { useSiteTranslation } from '../hooks/useSiteTranslation';
import axios from 'axios';
import { useLandingSections } from '../hooks/useLandingSections';
import EditableSection from '../components/Cms/EditableSection';
import { resolveIcon } from '../utils/iconCatalog';
import { getPostTitle } from '../i18n/localizeContent';
import { getAssetUrl } from '../utils/assetUrl';
import type { ResolvedSectionContent } from '../i18n/localizeContent';
import TestimonialCard from '../components/Testimonials/TestimonialCard';
import TestimonialForm from '../components/Testimonials/TestimonialForm';
import type { PublicTestimonial } from '../components/Testimonials/types';

interface HeroPost {
  _id: string;
  slug: string;
  title: string;
  featuredImage?: string;
  i18n?: { pt?: { title?: string }; en?: { title?: string }; es?: { title?: string } };
}

function extractYoutubeId(url: string): string | null {
  const match = url.match(/(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?|shorts)\/|.*[?&]v=)|youtu\.be\/)([^"&?/ ]{11})/);
  return match ? match[1] : null;
}

function isYoutubeShort(url: string): boolean {
  return /youtube\.com\/shorts\//.test(url);
}

const Home: React.FC = () => {
  const { ts, t, i18n } = useSiteTranslation();
  const { sections, refetch } = useLandingSections('home');
  const [testimonials, setTestimonials] = useState<PublicTestimonial[]>([]);
  const [testimonialsLoading, setTestimonialsLoading] = useState(true);
  const [showTestimonialForm, setShowTestimonialForm] = useState(false);
  const [testimonialSent, setTestimonialSent] = useState(false);

  useEffect(() => {
    axios.get('/testimonials').then((res) => {
      setTestimonials(res.data);
    }).catch(() => {
      setTestimonials([]);
    }).finally(() => {
      setTestimonialsLoading(false);
    });
  }, []);

  const heroWidget = sections['home.hero']?.widget;
  const heroCardFitsVideo =
    heroWidget?.mode === 'video' && (!!heroWidget.videoFile || (!!heroWidget.videoUrl && isYoutubeShort(heroWidget.videoUrl)));
  const [heroPosts, setHeroPosts] = useState<HeroPost[]>([]);

  useEffect(() => {
    if (heroWidget?.mode !== 'posts') return;
    axios.get('/posts?featured=true&limit=3').then((res) => {
      setHeroPosts(res.data.posts || []);
    }).catch(() => {
      setHeroPosts([]);
    });
  }, [heroWidget?.mode]);
  const heroFallback: ResolvedSectionContent = useMemo(
    () => ({
      title: ts('home.heroTitle1'), title2: ts('home.heroTitle2'), subtitle: ts('home.heroSubtitle'),
      paragraph1: '', paragraph2: '', visionTitle: '', visionText: '',
      ctaPrimaryLabel: ts('home.ctaStart'), ctaSecondaryLabel: ts('home.ctaLearn'),
      items: []
    }),
    [ts]
  );

  const whyFallback: ResolvedSectionContent = useMemo(
    () => ({
      title: ts('home.whyTitle'), title2: '', subtitle: ts('home.whySubtitle'),
      paragraph1: '', paragraph2: '', visionTitle: '', visionText: '', ctaPrimaryLabel: '', ctaSecondaryLabel: '',
      items: [
        { id: '0', icon: 'Globe', title: ts('home.featConsultTitle'), description: ts('home.featConsultDesc'), label: '', duration: '', features: [], popularVisas: [], requirements: [] },
        { id: '1', icon: 'FileText', title: ts('home.featDocsTitle'), description: ts('home.featDocsDesc'), label: '', duration: '', features: [], popularVisas: [], requirements: [] },
        { id: '2', icon: 'Shield', title: ts('home.featSecureTitle'), description: ts('home.featSecureDesc'), label: '', duration: '', features: [], popularVisas: [], requirements: [] },
        { id: '3', icon: 'Clock', title: ts('home.featFastTitle'), description: ts('home.featFastDesc'), label: '', duration: '', features: [], popularVisas: [], requirements: [] }
      ]
    }),
    [ts]
  );

  const servicesFallback: ResolvedSectionContent = useMemo(
    () => ({
      title: ts('home.servicesTitle'), title2: '', subtitle: ts('home.servicesSubtitle'),
      paragraph1: '', paragraph2: '', visionTitle: '', visionText: '', ctaPrimaryLabel: '', ctaSecondaryLabel: '',
      items: [
        {
          id: '0', title: ts('home.svcWorkTitle'), description: ts('home.svcWorkDesc'), label: '', duration: '',
          features: [ts('home.featEligibility'), ts('home.featPrepDocs'), ts('home.featFollowUp')], popularVisas: [], requirements: []
        },
        {
          id: '1', title: ts('home.svcStudyTitle'), description: ts('home.svcStudyDesc'), label: '', duration: '',
          features: [ts('home.featInstitutions'), ts('home.featVisaApp'), ts('home.featAcademic')], popularVisas: [], requirements: []
        },
        {
          id: '2', title: ts('home.svcCitTitle'), description: ts('home.svcCitDesc'), label: '', duration: '',
          features: [ts('home.featGenealogy'), ts('home.featHistorical'), ts('home.featLegal')], popularVisas: [], requirements: []
        }
      ]
    }),
    [ts]
  );

  const ctaFinalFallback: ResolvedSectionContent = useMemo(
    () => ({
      title: ts('home.ctaReadyTitle'), title2: '', subtitle: t('home.ctaReadySubtitle'),
      paragraph1: '', paragraph2: '', visionTitle: '', visionText: '',
      ctaPrimaryLabel: t('home.ctaExpert'), ctaSecondaryLabel: t('home.ctaArticles'),
      items: []
    }),
    [ts, t]
  );

  const statsFallback: ResolvedSectionContent = useMemo(
    () => ({
      title: '', title2: '', subtitle: '',
      paragraph1: '', paragraph2: '', visionTitle: '', visionText: '', ctaPrimaryLabel: '', ctaSecondaryLabel: '',
      items: [
        { id: '0', number: 500, numberSuffix: '+', title: '', description: '', label: ts('home.statFamilies'), duration: '', features: [], popularVisas: [], requirements: [] },
        { id: '1', number: 15, numberSuffix: '+', title: '', description: '', label: ts('home.statCountries'), duration: '', features: [], popularVisas: [], requirements: [] },
        { id: '2', number: 95, numberSuffix: '%', title: '', description: '', label: ts('home.statSuccess'), duration: '', features: [], popularVisas: [], requirements: [] },
        { id: '3', number: 24, numberSuffix: '/7', title: '', description: '', label: ts('home.statSupport'), duration: '', features: [], popularVisas: [], requirements: [] }
      ]
    }),
    [ts]
  );

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="hero-gradient relative overflow-hidden">
        <div className="absolute inset-0 bg-hero-pattern opacity-5"></div>
        <div className="container-custom section-padding relative">
          <EditableSection sectionKey="home.hero" data={sections['home.hero']} fallback={heroFallback} onSaved={refetch}>
            {(content) => (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <div>
                  <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                    {content.title}
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-600 to-secondary-600">
                      {' '}{content.title2}
                    </span>
                  </h1>
                  <p className="text-xl text-gray-600 mb-8 leading-relaxed">{content.subtitle}</p>
                  <div className="flex flex-col sm:flex-row gap-4 items-center sm:items-stretch">
                    <Link to="/contato" className="btn-primary text-lg px-8 py-4 flex items-center justify-center">
                      {content.ctaPrimaryLabel}
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Link>
                    <Link to="/sobre" className="btn-outline text-lg px-8 py-4 flex items-center justify-center">
                      {content.ctaSecondaryLabel}
                    </Link>
                  </div>
                </div>
                <div className={`relative ${heroCardFitsVideo ? 'w-fit mx-auto' : ''}`}>
                  {heroWidget?.mode === 'posts' ? (
                    <div className="bg-white rounded-2xl shadow-2xl p-6 transform rotate-3 hover:rotate-0 transition-transform duration-300">
                      <div className="text-sm font-semibold text-gray-500 mb-4 uppercase tracking-wide">{t('blog.featured')}</div>
                      {heroPosts.length > 0 ? (
                        <div className="space-y-3">
                          {heroPosts.slice(0, 3).map((post) => (
                            <Link key={post._id} to={`/post/${post.slug || post._id}`} className="flex items-center gap-3 group">
                              {post.featuredImage ? (
                                <img
                                  src={getAssetUrl(post.featuredImage)}
                                  alt={getPostTitle(post, i18n.language)}
                                  className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                                />
                              ) : (
                                <div className="w-12 h-12 rounded-lg bg-primary-100 flex-shrink-0" />
                              )}
                              <span className="text-sm font-medium text-gray-800 group-hover:text-primary-600 line-clamp-2">
                                {getPostTitle(post, i18n.language)}
                              </span>
                            </Link>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">{t('blog.emptyTitle')}</p>
                      )}
                    </div>
                  ) : heroWidget?.mode === 'video' ? (
                    <div className={`bg-white rounded-2xl shadow-2xl p-3 transform rotate-3 hover:rotate-0 transition-transform duration-300 overflow-hidden ${heroCardFitsVideo ? 'w-fit mx-auto' : ''}`}>
                      {heroWidget.videoFile ? (
                        <video src={getAssetUrl(heroWidget.videoFile)} controls className="block h-auto w-auto max-w-full max-h-[520px] rounded-lg" />
                      ) : heroWidget.videoUrl && extractYoutubeId(heroWidget.videoUrl) ? (
                        <iframe
                          className={`block rounded-lg ${isYoutubeShort(heroWidget.videoUrl) ? 'h-[520px] max-h-[70vh] w-auto max-w-full aspect-[9/16]' : 'w-full aspect-video'}`}
                          src={`https://www.youtube.com/embed/${extractYoutubeId(heroWidget.videoUrl)}`}
                          title={ts('contact.mapIframeTitle')}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      ) : (
                        <div className="aspect-video rounded-lg bg-gray-100 flex items-center justify-center text-gray-400">
                          <PlayCircle className="w-10 h-10" />
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="bg-white rounded-2xl shadow-2xl p-8 transform rotate-3 hover:rotate-0 transition-transform duration-300">
                      <div className="space-y-4">
                        <div className="flex items-center space-x-3">
                          <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                          <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                          <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                        </div>
                        <div className="space-y-3">
                          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                          <div className="h-4 bg-primary-200 rounded w-2/3"></div>
                        </div>
                        <div className="pt-4">
                          <div className="text-sm text-gray-500 mb-2">{ts('home.processLabel')}</div>
                          <div className="flex items-center space-x-2">
                            <div className="flex-1 bg-gray-200 rounded-full h-2">
                              <div className="bg-primary-600 h-2 rounded-full w-3/4"></div>
                            </div>
                            <span className="text-sm font-medium text-primary-600">75%</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="absolute -top-4 -right-4 bg-secondary-500 text-white p-3 rounded-full shadow-lg">
                    <CheckCircle className="w-6 h-6" />
                  </div>
                </div>
              </div>
            )}
          </EditableSection>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-white py-16">
        <div className="container-custom">
          <EditableSection sectionKey="home.stats" data={sections['home.stats']} fallback={statsFallback} onSaved={refetch}>
            {(content) => (
              <div className="grid-centered [--cols:2] md:[--cols:4]">
                {content.items.map((item) => (
                  <div key={item.id} className="text-center">
                    <div className="text-4xl font-bold text-primary-600 mb-2">
                      <AnimatedCounter
                        value={item.number || 0}
                        suffix={item.numberSuffix || ''}
                        className="text-4xl font-bold text-primary-600"
                      />
                    </div>
                    <div className="text-gray-600">{item.label}</div>
                  </div>
                ))}
              </div>
            )}
          </EditableSection>
        </div>
      </section>

      {/* Features Section */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <EditableSection sectionKey="home.why" data={sections['home.why']} fallback={whyFallback} onSaved={refetch}>
            {(content) => (
              <>
                <div className="text-center mb-16">
                  <h2 className="text-4xl font-bold text-gray-900 mb-6">{content.title}</h2>
                  <p className="text-xl text-gray-600 max-w-3xl mx-auto">{content.subtitle}</p>
                </div>

                <div className="grid-centered md:[--cols:2] lg:[--cols:4]">
                  {content.items.map((item) => {
                    const Icon = resolveIcon(item.icon);
                    return (
                      <div key={item.id} className="card text-center group hover:-translate-y-2 transition-transform duration-300">
                        <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-primary-600 transition-colors duration-300">
                          <Icon className="w-8 h-8 text-primary-600 group-hover:text-white transition-colors duration-300" />
                        </div>
                        <h3 className="text-xl font-semibold text-gray-900 mb-4">{item.title}</h3>
                        <p className="text-gray-600">{item.description}</p>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </EditableSection>
        </div>
      </section>

      {/* Services Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <EditableSection sectionKey="home.services" data={sections['home.services']} fallback={servicesFallback} onSaved={refetch}>
            {(content) => (
              <>
                <div className="text-center mb-16">
                  <h2 className="text-4xl font-bold text-gray-900 mb-6">{content.title}</h2>
                  <p className="text-xl text-gray-600 max-w-3xl mx-auto">{content.subtitle}</p>
                </div>

                <div className="grid-centered md:[--cols:3]">
                  {content.items.map((item) => (
                    <div key={item.id} className="card group hover:shadow-2xl transition-shadow duration-300">
                      <h3 className="text-2xl font-semibold text-gray-900 mb-4">{item.title}</h3>
                      <p className="text-gray-600 mb-6">{item.description}</p>
                      <ul className="space-y-2">
                        {item.features.map((feature, featureIndex) => (
                          <li key={featureIndex} className="flex items-center space-x-2">
                            <CheckCircle className="w-5 h-5 text-secondary-500 flex-shrink-0" />
                            <span className="text-gray-700">{feature}</span>
                          </li>
                        ))}
                      </ul>
                      <Link
                        to="/contato"
                        className="btn-primary w-full mt-6 text-center block group-hover:bg-primary-700 transition-colors duration-300"
                      >
                        {t('common.learnMore')}
                      </Link>
                    </div>
                  ))}
                </div>
              </>
            )}
          </EditableSection>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              {ts('home.testimonialsTitle')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {ts('home.testimonialsSubtitle')}
            </p>
          </div>

          {testimonialsLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" />
            </div>
          ) : (
            <>
              {testimonials.length === 0 ? (
                <div className="text-center py-8 px-4 rounded-xl bg-white/60 border border-gray-200">
                  <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-600 mb-4">{ts('home.testimonialsEmpty')}</p>
                  <p className="text-sm text-gray-500">{ts('home.testimonialsEmptyCta')}</p>
                </div>
              ) : (
                <div className="grid-centered md:[--cols:3]">
                  {testimonials.map((testimonial) => (
                    <TestimonialCard key={testimonial._id} item={testimonial} />
                  ))}
                </div>
              )}

              <div className="mt-12 flex justify-center">
                {!showTestimonialForm ? (
                  <div className="flex flex-col items-center gap-4">
                    {testimonialSent && (
                      <div className="p-3 bg-green-50 text-green-800 rounded-lg text-sm max-w-xl" role="status">
                        {t('testimonialsUi.success')}
                      </div>
                    )}
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                      <Link
                        to="/depoimentos"
                        className="btn-outline inline-flex items-center gap-2"
                      >
                        {t('testimonialsUi.viewAll')}
                      </Link>
                      <button
                        type="button"
                        onClick={() => { setShowTestimonialForm(true); setTestimonialSent(false); }}
                        className="btn-primary inline-flex items-center gap-2"
                      >
                        <MessageSquare className="w-5 h-5" />
                        {t('testimonialsUi.leaveCta')}
                      </button>
                    </div>
                  </div>
                ) : (
                  <TestimonialForm
                    onCancel={() => setShowTestimonialForm(false)}
                    onSuccess={() => { setShowTestimonialForm(false); setTestimonialSent(true); }}
                  />
                )}
              </div>
            </>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-r from-primary-600 to-secondary-600 text-white">
        <div className="container-custom text-center">
          <EditableSection sectionKey="home.ctaFinal" data={sections['home.ctaFinal']} fallback={ctaFinalFallback} onSaved={refetch}>
            {(content) => (
              <>
                <h2 className="text-4xl font-bold mb-6">{content.title}</h2>
                <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">{content.subtitle}</p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                  <Link to="/contato" className="bg-white text-primary-600 hover:bg-gray-100 font-semibold py-3 px-8 rounded-lg transition-colors duration-200 flex items-center justify-center">
                    {content.ctaPrimaryLabel}
                  </Link>
                  <Link to="/blog" className="border-2 border-white text-white hover:bg-white hover:text-primary-600 font-semibold py-3 px-8 rounded-lg transition-all duration-200 flex items-center justify-center">
                    {content.ctaSecondaryLabel}
                  </Link>
                </div>
              </>
            )}
          </EditableSection>
        </div>
      </section>
    </div>
  );
};

export default Home;
